import { useEffect, useMemo, useState } from 'react';

import { CustomerListMatchingAttribute } from '~/types/api.graphql';
import {
  buildMappingValue,
  buildSimpleRules,
  canAddRule,
  chooseInitialView,
  CustomerListColumn,
  CustomerListColumnTypes,
  customerListMappingStartSchema,
  CustomerListMatchingLimits,
  CustomerListRuleDraft,
  findIdentifierColumns,
  findMatchingRulesIssues,
  findRulesDroppedBySimpleView,
  isIdentifierAttribute,
  MatchingRulesView,
  parseJsonProp,
  readMappingValue,
} from '~/utils/customerListMatchingRules';

export type { MatchingRulesView };

type Options = {
  attachmentId: string;
  columns: CustomerListColumn[];
  limits: CustomerListMatchingLimits;
  initialView?: MatchingRulesView;
  value?: string;
  setValue: (value: string) => void;
};

/**
 * The mapping form's state: one type per column, the simple view's ticked columns and, in the custom view, the rules
 * being edited. Writes the submitted value as the user edits, and an empty one while the form can't be continued, so
 * the surface's `required` check blocks submit.
 */
export const useMatchingRulesState = ({ attachmentId, columns, limits, initialView, value, setValue }: Options) => {
  const { identifierAttributes } = limits;
  const [initialState] = useState(() => readInitialState({ attachmentId, columns, limits, initialView, value }));
  const [view, setView] = useState<MatchingRulesView>(initialState.view);
  const [types, setTypes] = useState<CustomerListColumnTypes>(initialState.types);
  const [tickedColumns, setTickedColumns] = useState<number[]>(initialState.rules.flat());
  const [customRules, setCustomRules] = useState<CustomerListRuleDraft[]>(initialState.rules);
  const [changedColumns, setChangedColumns] = useState<Set<number>>(new Set());
  const [expandedRule, setExpandedRule] = useState<number | null>(null);
  const [rulesToDrop, setRulesToDrop] = useState<number[] | null>(null);
  // A column ticked in the simple view with a profile attribute, waiting for the user to confirm or cancel
  const [warningColumn, setWarningColumn] = useState<number | null>(null);

  const rules = useMemo(
    () => (view === 'simple' ? buildSimpleRules(columns, tickedColumns) : customRules),
    [view, columns, tickedColumns, customRules],
  );
  const issues = useMemo(
    () => findMatchingRulesIssues({ attachmentId, columns, types, rules, limits }),
    [attachmentId, columns, types, rules, limits],
  );
  const submittedValue = issues.length
    ? ''
    : JSON.stringify(buildMappingValue({ attachmentId, columns, types, rules, limits }));

  useEffect(() => {
    if (submittedValue !== value) setValue(submittedValue);
    // Only the form's own changes write the value
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submittedValue]);

  // The rules a pending "Back to simple matching" would drop are positions in the rules being edited, so any edit asks
  // again
  const editRules = (nextRules: CustomerListRuleDraft[]) => {
    setRulesToDrop(null);
    setCustomRules(nextRules);
  };

  const tickInSimpleView = (columnIndex: number, attribute: CustomerListMatchingAttribute | undefined) => {
    setTickedColumns((current) => (current.includes(columnIndex) ? current : [...current, columnIndex]));
    if (attribute && !isIdentifierAttribute(attribute, identifierAttributes)) setWarningColumn(columnIndex);
  };

  const untickInSimpleView = (columnIndex: number) =>
    setTickedColumns((current) => current.filter((ticked) => ticked !== columnIndex));

  // Picking a type means the column is used: it is ticked in the simple view, or in the expanded rule
  const setType = (columnIndex: number, attribute: CustomerListMatchingAttribute | undefined) => {
    setRulesToDrop(null);
    setTypes((current) => ({ ...current, [columnIndex]: attribute }));
    setChangedColumns((current) => new Set(current).add(columnIndex));

    if (view === 'simple') {
      if (attribute) tickInSimpleView(columnIndex, attribute);
      else untickInSimpleView(columnIndex);
      return;
    }
    if (expandedRule === null) return;
    editRules(
      customRules.map((rule, index) => {
        if (index !== expandedRule) return rule;
        if (!attribute) return rule.filter((ticked) => ticked !== columnIndex);
        return rule.includes(columnIndex) ? rule : [...rule, columnIndex];
      }),
    );
  };

  const toggleSimpleColumn = (columnIndex: number) => {
    if (tickedColumns.includes(columnIndex)) untickInSimpleView(columnIndex);
    else tickInSimpleView(columnIndex, types[columnIndex]);
  };

  const openCustomView = (nextRules: CustomerListRuleDraft[], nextExpandedRule: number | null) => {
    setCustomRules(nextRules);
    setExpandedRule(nextExpandedRule);
    setView('custom');
  };

  const customize = () => openCustomView(buildSimpleRules(columns, tickedColumns), null);

  // The warned column becomes a rule of its own, last and expanded, for the user to add columns to
  const confirmWarning = () => {
    if (warningColumn === null) return;
    const otherRules = buildSimpleRules(
      columns,
      tickedColumns.filter((ticked) => ticked !== warningColumn),
    );
    openCustomView([...otherRules, [warningColumn]], otherRules.length);
    setWarningColumn(null);
  };

  // The type stays, so the user can pick another one if the suggested one was wrong
  const cancelWarning = () => {
    if (warningColumn !== null) untickInSimpleView(warningColumn);
    setWarningColumn(null);
  };

  const addRule = () => {
    if (!canAddRule(customRules, limits)) return;
    editRules([...customRules, []]);
    setExpandedRule(customRules.length);
  };

  const deleteRule = (ruleIndex: number) => {
    editRules(customRules.filter((_, index) => index !== ruleIndex));
    setExpandedRule(null);
  };

  // Not blocked at the field limit: a rule over it shows the limit as a problem
  const toggleColumn = (ruleIndex: number, columnIndex: number) =>
    editRules(
      customRules.map((rule, index) => {
        if (index !== ruleIndex) return rule;
        return rule.includes(columnIndex) ? rule.filter((ticked) => ticked !== columnIndex) : [...rule, columnIndex];
      }),
    );

  // Keeps only the one-column identifier rules, ticked; every other column is unticked, keeping its type
  const goToSimple = (droppedRules: number[]) => {
    const keptRules = customRules.filter((_, ruleIndex) => !droppedRules.includes(ruleIndex));
    setTickedColumns([...new Set(keptRules.flat())]);
    setRulesToDrop(null);
    setView('simple');
  };

  const backToSimple = () => {
    const droppedRules = findRulesDroppedBySimpleView(customRules, types, identifierAttributes);
    if (droppedRules.length) {
      setRulesToDrop(droppedRules);
      return;
    }
    goToSimple([]);
  };

  const confirmBackToSimple = () => goToSimple(rulesToDrop ?? []);

  return {
    view,
    types,
    rules,
    issues,
    changedColumns,
    expandedRule,
    rulesToDrop,
    warningColumn,
    setType,
    setExpandedRule,
    toggleSimpleColumn,
    confirmWarning,
    cancelWarning,
    customize,
    addRule,
    deleteRule,
    toggleColumn,
    backToSimple,
    confirmBackToSimple,
    cancelBackToSimple: () => setRulesToDrop(null),
  };
};

const readInitialState = ({ attachmentId, columns, limits, initialView, value }: Omit<Options, 'setValue'>) => {
  const earlierValue = parseJsonProp(customerListMappingStartSchema, value);
  if (earlierValue?.attachmentId === attachmentId) {
    const { types, rules } = readMappingValue(earlierValue, columns);
    return {
      view: chooseInitialView({
        rules,
        types,
        identifierAttributes: limits.identifierAttributes,
        requestedView: initialView,
      }),
      types,
      rules,
    };
  }

  const types: CustomerListColumnTypes = Object.fromEntries(
    columns.flatMap(({ index, suggestedAttribute }) => (suggestedAttribute ? [[index, suggestedAttribute]] : [])),
  );
  return {
    view: initialView ?? 'simple',
    types,
    rules: buildSimpleRules(columns, findIdentifierColumns(columns, types, limits.identifierAttributes)),
  };
};
